// packages/shared-components/src/components/Keyboard/iconConfig.ts
import {
  getRongyuanUiKeyLabel,
  RONGYUAN_UI_DISABLED,
} from "@eweadn-qiankun/shared-utils";
import { getKeyCodeInfo } from "../../utils/keyCodeMapper";

/** 星闪 / 华奋达 / 贝盈等方案沿用的扩展键 iconfont */
const BASE_ICON_CONFIG: Record<number, string> = {
  8304: "dengguangliangdu--1",
  8303: "a-dengguangliangdu-1",
  8594: "jisuanji",
  8595: "boardconfig",
  8596: "wodediannao",
  33554826: "youjian",
  8374: "shangyishou",
  8375: "tingzhi",
  8397: "bofang1",
  8373: "xiayishou",
  8418: "jingyin1",
  8426: "yinliang-",
  8425: "a-yinliang",
  8579: "yinle",
  33554819: "yinle",
  8739: "liulanqi",
  62208: "dengguangmoshiqiehuan",
  62224: "zhuangshideng-moshiqiehuan",
  62225: "zhuangshideng-yanseqiehuan",
  62209: "dengguangyansemoshiqiehuan",
  61961: "winlock",
  62210: "dengxiaoliangdujia",
  62211: "dengxiaoliangdujian",
  62212: "a-dengguangsudu",
  62213: "dengguangsudu-",
  62214: "dengguangshangyigemoshi",
  62215: "dengguangshangyigemoshi-1",
  62216: "dengguangfangxiang",
  62226: "d1-bri-plus",
  62227: "d1-bri-less",
  62228: "d1-spd-plus",
  62229: "d1-spd-less",
  62230: "d1-prev",
  62231: "d1-on-off",
  62232: "d1-dir",
  16384: "shubiaoshifang",
  16640: "shubiaozuojian",
  16896: "shubiaoyoujian",
  17152: "zhongjian",
  17920: "shubiaowangzuo",
  18176: "shubiaowangyou",
  18432: "shubiaowangshang",
  18688: "shubiaowangxia",
  18945: "zhongjian-wangshang",
  19201: "zhongjian-wangxia",
  61952: "shezhi1",
  61953: "win",
  61954: "pingguo",
  61957: "qiehuanpeizhi",
  61958: "qiehuanpeizhi-1",
  61959: "qiehuanpeizhi-2",
  61960: "qiehuanpeizhi-3",
};

/**
 * 容园扩展键中文名 → iconfont（与星闪/华奋达语义对齐）
 * 用于 packed UI 键码在 ICON_CONFIG 未直填时的兜底。
 */
export const RONGYUAN_LABEL_TO_ICON: Record<string, string> = {
  上一曲: "shangyishou",
  下一曲: "xiayishou",
  停止: "tingzhi",
  "播放/暂停": "bofang1",
  静音: "jingyin1",
  音量减: "yinliang-",
  音量加: "a-yinliang",
  播放器: "yinle",
  计算器: "jisuanji",
  邮件: "youjian",
  我的电脑: "wodediannao",
  搜索: "liulanqi",
  主页: "liulanqi",
  返回: "liulanqi",
  刷新: "liulanqi",
  亮度减: "dengguangliangdu--1",
  亮度加: "a-dengguangliangdu-1",
  灯效开关: "d1-on-off",
  灯效模式: "dengguangmoshiqiehuan",
  "灯效选项+": "dengguangshangyigemoshi",
  "灯效选项-": "dengguangshangyigemoshi-1",
  "回报率+": "d1-spd-plus",
  "回报率-": "d1-spd-less",
  一键白光: "d1-bri-plus",
  FN锁屏: "winlock",
  锁屏: "winlock",
  锁定Win: "winlock",
  "锁定 Win": "winlock",
  LockWin: "winlock",
  切换输入法: "boardconfig",
  "Profile+": "qiehuanpeizhi",
  "Profile-": "qiehuanpeizhi-1",
  Profile循环: "qiehuanpeizhi-2",
  Profile切换: "qiehuanpeizhi",
  Profile切换2: "qiehuanpeizhi-1",
  Profile切换3: "qiehuanpeizhi-2",
  Profile切换4: "qiehuanpeizhi-3",
  Profile切换5: "qiehuanpeizhi-3",
  "DPI+": "d1-spd-plus",
  "DPI-": "d1-spd-less",
  DPI循环: "qiehuanpeizhi-2",
  鼠标左键: "shubiaozuojian1",
  鼠标右键: "shubiaoyoujian",
  鼠标中键: "zhongjian",
  鼠标后退: "shubiaowangzuo",
  鼠标前进: "shubiaowangyou",
  滚轮向上: "zhongjian-wangshang",
  滚轮向下: "zhongjian-wangxia",
  滚轮左: "shubiaowangzuo",
  滚轮右: "shubiaowangyou",
  滚轮前: "zhongjian-wangshang",
  滚轮后: "zhongjian-wangxia",
  X轴向右: "shubiaowangyou",
  X轴向左: "shubiaowangzuo",
  Y轴向下: "shubiaowangxia",
  Y轴向上: "shubiaowangshang",
  放大: "fangda",
  缩小: "suoxiao",
  麦克风开关: "maikefeng",
};

/** 容园 packed UI 键码 → iconfont（运行时由 label 兜底，此处仅保留高频直查） */
const RONGYUAN_ICON_CONFIG: Record<number, string> = {};

export const ICON_CONFIG: Record<number, string> = {
  ...BASE_ICON_CONFIG,
  ...RONGYUAN_ICON_CONFIG,
};

/** 按键 UI 码 → iconfont；兼容容园 packed 键码 */
export function resolveKeyboardKeyIcon(keyCode: number): string | undefined {
  if (keyCode === RONGYUAN_UI_DISABLED) return undefined;
  const direct = ICON_CONFIG[keyCode];
  if (direct) return direct;

  const label = getRongyuanUiKeyLabel(keyCode);
  if (label && RONGYUAN_LABEL_TO_ICON[label]) {
    return RONGYUAN_LABEL_TO_ICON[label];
  }
  return undefined;
}

/** 无图标时的键帽文案：优先布局 label，再容园协议名、KEYCODE_MAP */
export function resolveKeyboardKeyTextLabel(
  keyCode: number,
  layoutLabel?: string
): string {
  if (keyCode === RONGYUAN_UI_DISABLED) return "";
  const fromLayout = String(layoutLabel ?? "").trim();
  if (fromLayout) return fromLayout;

  const rongyuanLabel = getRongyuanUiKeyLabel(keyCode);
  if (rongyuanLabel) return rongyuanLabel;

  const mapInfo = getKeyCodeInfo(keyCode);
  if (mapInfo?.name) return mapInfo.name;

  return "";
}
